import { dom } from "../dom";
import { state } from "../state";
import { ifcLoader } from "../viewer/scene";
import { getIfcName, unwrapIfcValue } from "../ifc/props";
import {
  refreshSelectionInfo,
  updatePickHighlight,
  updateVisibilityButtons,
} from "./selection";
import { refreshPropertiesPanel } from "./properties";
import { focusOnExpressIds } from "./viewerCamera";
import { escapeHtmlText } from "../utils/html";
import { setStatus } from "../ui/status";

interface SearchResult {
  expressId: number;
  type: string;
  name: string;
  tag: string;
  globalId: string;
}

export function updateSearchUiState(): void {
  const hasModel = Boolean(state.ifcModel);
  dom.searchInput.disabled = !hasModel;
  dom.searchRunButton.disabled = !hasModel;
  if (!hasModel) {
    dom.searchResultsElement.innerHTML = "";
    dom.searchStatusElement.textContent = "Загрузите IFC, чтобы искать элементы.";
  } else {
    dom.searchStatusElement.textContent = "Введите запрос для поиска.";
  }
}

const MAX_RESULTS = 100;

interface MetaCandidate {
  expressId: number;
  type: string;
  mark: string;
  storey: string;
}

function gatherMetaCandidates(): MetaCandidate[] {
  const out: MetaCandidate[] = [];
  for (const [expressId, meta] of state.elementMeta) {
    out.push({
      expressId,
      type: meta.type || "",
      mark: meta.mark || "",
      storey: meta.storeyName || "",
    });
  }
  return out;
}

/**
 * Универсальный поиск: сначала фильтрует по уже посчитанному `elementMeta`
 * (type, mark, storey, expressId) — это синхронно и быстро.
 * Затем для выбранных матчей подгружает GlobalId, чтобы показать его в UI.
 *
 * Если запрос похож на IFC GUID (22 символа, base64-like) или начинается с
 * "0", то делается дополнительный проход по props для поиска по GlobalId.
 */
export async function runSearch(): Promise<void> {
  if (!state.ifcModel) {
    return;
  }
  const queryRaw = dom.searchInput.value.trim();
  if (!queryRaw) {
    dom.searchStatusElement.textContent = "Введите запрос для поиска.";
    dom.searchResultsElement.innerHTML = "";
    return;
  }
  const queryLower = queryRaw.toLowerCase();
  dom.searchStatusElement.textContent = "Поиск...";
  dom.searchResultsElement.innerHTML = "";

  const metaHits: MetaCandidate[] = [];
  for (const candidate of gatherMetaCandidates()) {
    const haystack = [
      candidate.type,
      candidate.mark,
      candidate.storey,
      String(candidate.expressId),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (haystack.includes(queryLower)) {
      metaHits.push(candidate);
      if (metaHits.length >= MAX_RESULTS) {
        break;
      }
    }
  }

  // Если запрос похож на GUID (22 символа, без пробелов) — добиваем поиск
  // через props (по GlobalId). Это медленнее, поэтому делается отдельно.
  const guidLike = /^[A-Za-z0-9_$]{20,24}$/.test(queryRaw);
  if (guidLike && state.elementMeta.size > 0) {
    const modelId = state.ifcModel.modelID;
    const seen = new Set(metaHits.map((h) => h.expressId));
    for (const [expressId, meta] of state.elementMeta) {
      if (seen.has(expressId) || metaHits.length >= MAX_RESULTS) {
        break;
      }
      try {
        const props = await ifcLoader.ifcManager.getItemProperties(modelId, expressId, false);
        const gid = unwrapIfcValue(props?.GlobalId);
        if (gid && gid.toLowerCase().includes(queryLower)) {
          metaHits.push({
            expressId,
            type: meta.type || "",
            mark: meta.mark || "",
            storey: meta.storeyName || "",
          });
        }
      } catch (_) {
        /* skip */
      }
    }
  }

  if (metaHits.length === 0) {
    dom.searchStatusElement.textContent = "Ничего не найдено.";
    return;
  }

  // Подгружаем подробные поля (name, tag, globalId) только для матчей,
  // чтобы показать их в карточке результата.
  const modelId = state.ifcModel.modelID;
  const enriched: SearchResult[] = [];
  for (const hit of metaHits) {
    let name = hit.mark;
    let tag = "";
    let globalId = "";
    try {
      const props = await ifcLoader.ifcManager.getItemProperties(
        modelId,
        hit.expressId,
        false,
      );
      name = getIfcName(props) || hit.mark || "";
      tag = unwrapIfcValue(props?.Tag);
      globalId = unwrapIfcValue(props?.GlobalId);
    } catch (_) {
      /* keep meta values */
    }
    enriched.push({ expressId: hit.expressId, type: hit.type, name, tag, globalId });
  }

  dom.searchStatusElement.textContent = `Найдено: ${enriched.length}${
    enriched.length >= MAX_RESULTS ? " (показаны первые " + MAX_RESULTS + ")" : ""
  }.`;
  dom.searchResultsElement.innerHTML = enriched
    .map(
      (h) =>
        `<div class="search-result-item" data-search-id="${h.expressId}">` +
        `<div><div><strong>${escapeHtmlText(h.name || `#${h.expressId}`)}</strong></div>` +
        `<div class="search-result-meta">${escapeHtmlText(h.type)}${h.tag ? ` · Tag: ${escapeHtmlText(h.tag)}` : ""}${h.globalId ? `<br>${escapeHtmlText(h.globalId)}` : ""}</div></div>` +
        `<button type="button" data-search-focus="${h.expressId}">Фокус</button>` +
        "</div>",
    )
    .join("");
}

async function selectAndShowOne(expressId: number, focus: boolean): Promise<void> {
  if (!state.ifcModel) {
    return;
  }
  state.selectedExpressIds.clear();
  state.selectedExpressIds.add(expressId);
  updatePickHighlight();
  updateVisibilityButtons();
  await refreshSelectionInfo();
  await refreshPropertiesPanel();
  if (focus) {
    focusOnExpressIds([expressId]);
  }
}

export async function handleSearchResultsClick(event: MouseEvent): Promise<void> {
  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  const focusBtn = target.closest<HTMLElement>("[data-search-focus]");
  if (focusBtn) {
    const id = Number(focusBtn.dataset.searchFocus);
    if (Number.isFinite(id)) {
      await selectAndShowOne(id, true);
      setStatus(`Элемент ${id} выбран и приближен.`);
    }
    return;
  }
  const item = target.closest<HTMLElement>("[data-search-id]");
  if (item) {
    const id = Number(item.dataset.searchId);
    if (Number.isFinite(id)) {
      await selectAndShowOne(id, false);
      setStatus(`Элемент ${id} выделен.`);
    }
  }
}
